import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuDownload, LuLink, LuShare2 } from "react-icons/lu";
import { drawNameCard, canvasToBlob } from "../utils/nameCard";

const ShareCardModal = ({ name, onClose, getGenderLabel, syriacFontType }) => {
  const canvasRef = useRef(null);
  const [isRendering, setIsRendering] = useState(true);
  const [status, setStatus] = useState("");
  const engName = (name.eng_word || "").trim();

  const shareUrl = `https://names.betkanu.com/?search=${encodeURIComponent(
    engName,
  )}`;

  useEffect(() => {
    let cancelled = false;
    setIsRendering(true);
    drawNameCard(canvasRef.current, {
      name,
      getGenderLabel,
      syriacFontType,
      shareUrl,
    }).finally(() => {
      if (!cancelled) setIsRendering(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, syriacFontType]);

  const fileName = `${(engName || "name").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-BETKANU-name-card.png`;

  const downloadImage = async () => {
    const blob = await canvasToBlob(canvasRef.current);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setStatus("Link copied to clipboard!");
    } catch {
      setStatus(
        "Couldn't copy the link — copy it from the address shown above.",
      );
    }
  };

  const handleShare = async () => {
    setStatus("");
    const blob = await canvasToBlob(canvasRef.current);
    const title = `${engName} — Name Meaning`;
    const nameAndMeaning = name.meaning
      ? `${engName}: ${name.meaning}`
      : engName;

    try {
      if (blob) {
        const file = new File([blob], fileName, { type: "image/png" });
        // Deliberately plain here — no URL, and no domain-like text such as
        // "betkanu.com/names". Facebook in particular treats any link-like
        // string as a request to build a link-preview post and then drops
        // the attached image entirely (posting nothing), while WhatsApp and
        // Instagram don't do this. The card already prints the site link on
        // itself, so the image travels alone.
        const fileShareData = { title, text: nameAndMeaning, files: [file] };
        if (navigator.canShare && navigator.canShare(fileShareData)) {
          await navigator.share(fileShareData);
          setStatus("Shared!");
          return;
        }
      }
      if (navigator.share) {
        const text = `${nameAndMeaning}\n\nExplore more names at betkanu.com/names`;
        await navigator.share({ title, text, url: shareUrl });
        setStatus("Shared!");
        return;
      }
      // No Web Share API support (e.g. desktop browsers): fall back to a
      // direct download plus a copied link so the user can post it manually.
      if (blob) await downloadImage();
      await copyLink();
      setStatus("Image downloaded and link copied — ready to post!");
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);
        setStatus("Something went wrong sharing this card.");
      }
    }
  };

  return createPortal(
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup-box modal-margins share-card-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="popup-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
        <h2>Share Name Card</h2>
        <div className="name-card-preview-wrap">
          {isRendering && (
            <div className="name-card-preview-loading">Rendering card…</div>
          )}
          <canvas ref={canvasRef} className="name-card-canvas" />
        </div>
        <div className="share-card-actions">
          <button className="reset-btn share-card-btn" onClick={handleShare}>
            <LuShare2 size={20} />
            Share
          </button>
          <button className="share-card-btn-secondary" onClick={downloadImage}>
            <LuDownload size={18} />
            Download
          </button>
          <button className="share-card-btn-secondary" onClick={copyLink}>
            <LuLink size={18} />
            Copy Link
          </button>
        </div>
        {status && <div className="share-card-status">{status}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default ShareCardModal;
